import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api, { importOFX } from '../services/api';
import ConfirmModal from './ConfirmModal';
import TransactionModal from './TransactionModal';

import { DayPicker } from 'react-day-picker';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import SkeletonLoader from './SkeletonLoader';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';

function TransactionsPage() {
  const PAGE_SIZE = 20;

  // New UI states
  const [activeSubnav, setActiveSubnav] = useState('Historico');
  const [activeTab, setActiveTab] = useState('Lancamentos');

  // --- SEARCH & LOCATION ---
  const [searchParams] = useSearchParams();
  const locationState = useLocation();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');

  // --- TRANSACTIONS STATE ---
  const [transactions, setTransactions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [transactionToEdit, setTransactionToEdit] = useState(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // --- RECURRING STATE ---
  const [recurringTransactions, setRecurringTransactions] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isLoadingRecurring, setIsLoadingRecurring] = useState(false);
  const [recurringFormState, setRecurringFormState] = useState({
    title: '', amount: '', type: 'EXPENSE', category_id: '',
    frequency: 'MONTHLY', day_of_month: '1',
    start_date: format(new Date(), 'yyyy-MM-dd')
  });
  const [isRecurringConfirmOpen, setIsRecurringConfirmOpen] = useState(false);
  const [recurringToDelete, setRecurringToDelete] = useState(null);

  const navigate = useNavigate();

  // Sync search term when URL param changes
  useEffect(() => {
    const q = searchParams.get('search') || '';
    setSearchTerm(q);
  }, [searchParams]);

  // Open "Nova Receita" modal if navigated with openAddIncome state
  const [defaultType, setDefaultType] = useState('EXPENSE');
  useEffect(() => {
    if (locationState.state?.openAddIncome) {
      setDefaultType('INCOME');
      setTransactionToEdit(null);
      setIsTransactionModalOpen(true);
      navigate(locationState.pathname, { replace: true, state: {} });
    }
  }, [locationState, navigate]);

  const fetchTransactions = useCallback(async (month, page, tab, search) => {
    setIsLoading(true);
    if (!month) return;
    const firstDay = startOfMonth(month);
    const lastDay = endOfMonth(month);
    try {
      const params = new URLSearchParams({
        start_date: format(firstDay, 'y-MM-dd'),
        end_date: format(lastDay, 'y-MM-dd'),
        page: String(page || 1),
        per_page: String(PAGE_SIZE)
      });

      if (tab === 'Pendentes') {
        params.append('status', 'PENDING');
      }
      if (search) {
        params.append('search', search);
      }

      const response = await api.get(`/transactions/?${params.toString()}`);
      const isPaginated = response.data && Array.isArray(response.data.items);

      if (isPaginated) {
        const pagination = response.data.pagination || {};
        setTransactions(response.data.items);
        setCurrentPage(pagination.page || 1);
        setTotalPages(pagination.total_pages || 1);
        setTotalItems(pagination.total || response.data.items.length);
      } else {
        setTransactions(response.data || []);
        setTotalItems((response.data || []).length);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (error) {
      toast.error("Erro ao buscar transações.");
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTransactions(selectedMonth, currentPage, activeTab, searchTerm);
  }, [selectedMonth, currentPage, activeTab, searchTerm, fetchTransactions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth, activeTab, searchTerm]);

  const handleAddTransaction = () => {
    setTransactionToEdit(null);
    setIsTransactionModalOpen(true);
  };

  const handleEdit = (transaction) => {
    setTransactionToEdit(transaction);
    setIsTransactionModalOpen(true);
    window.scrollTo(0, 0);
  };

  const handleSaveTransaction = async (transactionData, id) => {
    const loadingToast = toast.loading(id ? 'Salvando alterações...' : 'Adicionando transação...');
    try {
      if (id) {
        await api.put(`/transactions/${id}`, transactionData);
        toast.success('Transação atualizada!');
      } else {
        await api.post('/transactions/', transactionData);
        toast.success('Transação adicionada!');
      }
      setIsTransactionModalOpen(false);
      fetchTransactions(selectedMonth, currentPage, activeTab, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar transação.');
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleDelete = (id) => {
    setTransactionToDelete(id);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (transactionToDelete) {
      const loadingToast = toast.loading('Deletando...');
      try {
        await api.delete(`/transactions/${transactionToDelete}`);
        toast.success('Transação deletada!');
        fetchTransactions(selectedMonth, currentPage, activeTab, searchTerm);
      } catch (error) {
        toast.error(error.response?.data?.error || 'Erro ao deletar.');
      } finally {
        toast.dismiss(loadingToast);
        setIsConfirmModalOpen(false);
        setTransactionToDelete(null);
      }
    }
  };

  const handleConfirm = async (id) => {
    const loadingToast = toast.loading('Confirmando...');
    try {
      await api.patch(`/transactions/${id}/confirm`);
      toast.success('Transação confirmada!');
      fetchTransactions(selectedMonth, currentPage, activeTab, searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao confirmar.');
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // --- RECURRING LOGIC ---
  const fetchRecurringData = useCallback(async () => {
    setIsLoadingRecurring(true);
    try {
      const [recurringRes, categoriesRes] = await Promise.all([
        api.get('/recurring/'),
        api.get('/categories/')
      ]);
      setRecurringTransactions(recurringRes.data);
      setAvailableCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Não foi possível carregar as recorrências.');
    } finally {
      setIsLoadingRecurring(false);
    }
  }, []);

  useEffect(() => {
    if (activeSubnav === 'Planos') {
      fetchRecurringData();
    }
  }, [activeSubnav, fetchRecurringData]);

  const handleRecurringFormChange = (e) => {
    setRecurringFormState({ ...recurringFormState, [e.target.name]: e.target.value });
  };

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    const { title, amount, category_id, day_of_month, start_date } = recurringFormState;
    if (!title || !amount || !category_id || !day_of_month || !start_date) {
      toast.error('Por favor, preencha todos os campos.'); return;
    }
    const loadingToast = toast.loading('Criando regra de recorrência...');
    try {
      const response = await api.post('/recurring/', {
        ...recurringFormState,
        amount: parseFloat(amount),
        category_id: parseInt(category_id),
        day_of_month: parseInt(day_of_month)
      });
      const newRecurrenceId = response.data.recurring_transaction.id;
      toast.dismiss(loadingToast);
      toast.success('Regra recorrente criada!');
      
      const startDateObj = new Date(start_date.replace(/-/g, '/'));
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (startDateObj <= today) {
        const triggerToast = toast.loading('Criando primeira transação...');
        await api.post(`/recurring/${newRecurrenceId}/trigger`);
        toast.dismiss(triggerToast);
        toast.success('Primeira transação já foi lançada!');
      }
      fetchRecurringData();
      setRecurringFormState({
        title: '', amount: '', type: 'EXPENSE', category_id: '',
        frequency: 'MONTHLY', day_of_month: '1',
        start_date: format(new Date(), 'yyyy-MM-dd')
      });
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.error || 'Erro ao criar lançamento.');
    }
  };

  const startDeleteRecurring = (item) => {
    setRecurringToDelete(item);
    setIsRecurringConfirmOpen(true);
  };

  const confirmDeleteRecurring = async () => {
    if (recurringToDelete) {
      const loadingToast = toast.loading('Deletando...');
      try {
        await api.delete(`/recurring/${recurringToDelete.id}`);
        fetchRecurringData();
        toast.dismiss(loadingToast);
        toast.success('Recorrência deletada!');
      } catch (error) {
        toast.dismiss(loadingToast);
        toast.error('Erro ao deletar.');
      } finally {
        setIsRecurringConfirmOpen(false);
        setRecurringToDelete(null);
      }
    }
  };

  const fileInputRef = useRef(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const toastId = toast.loading("Importando extrato...");
      const result = await importOFX(file);
      toast.success(result.message, { id: toastId });
      fetchTransactions(selectedMonth, currentPage, activeTab, searchTerm);
    } catch (error) {
      toast.error(error.message);
    } finally {
      event.target.value = null;
    }
  };

  // Helper variables for calculated balance logic
  const totalIn = transactions.filter(t => t.type === 'INCOME').reduce((acc, t) => acc + parseFloat(t.amount || 0), 0);
  const totalOut = transactions.filter(t => t.type === 'EXPENSE').reduce((acc, t) => acc + parseFloat(t.amount || 0), 0);
  const monthlyBalance = totalIn - totalOut;
  const isPositiveBalance = monthlyBalance >= 0;

  // Render variables
  const filteredTransactions = transactions;

  return (
    <div className="font-inter w-full max-w-7xl mx-auto pb-20 relative">

      {/* Hidden file input */}
      <input type="file" accept=".ofx" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileUpload} />

      {/* Search active banner */}
      {searchTerm && (
        <div className="flex items-center justify-between mb-4 px-4 py-2.5 bg-indigo-50 border border-indigo-100 rounded-xl text-sm">
          <span className="text-indigo-700 font-medium">
            <span className="font-bold">Buscando:</span> "{searchTerm}" — {totalItems} resultado(s)
          </span>
          <button
            onClick={() => { setSearchTerm(''); navigate('/app/transactions', { replace: true }); }}
            className="text-indigo-500 hover:text-indigo-800 font-bold flex items-center gap-1"
          >
            <span className="material-symbols-outlined text-sm">close</span> Limpar
          </button>
        </div>
      )}

      {/* TOP NAVIGATION */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-12 bg-white/50 border-b border-slate-200 pb-4 pt-2 -mt-6 mx-[-2rem] px-8 sticky top-0 z-20 backdrop-blur-md gap-2">
        <div className="flex items-center justify-between sm:justify-start sm:space-x-8">
           <h1 className="text-xl font-bold text-slate-900 font-manrope">Transações</h1>
           <div className="flex space-x-4 sm:space-x-6 text-sm font-semibold overflow-x-auto no-scrollbar">
             {[
               { key: 'Resumo', label: 'Resumo' },
               { key: 'Historico', label: 'Histórico' },
               { key: 'Planos', label: 'Planos Recorrentes' },
             ].map(item => (
                <button
                  key={item.key}
                  onClick={() => setActiveSubnav(item.key)}
                  className={`py-4 -mb-4 whitespace-nowrap transition-colors ${activeSubnav === item.key ? 'text-[#4f46e5] border-b-2 border-[#4f46e5]' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  {item.label}
                </button>
             ))}
           </div>
        </div>
        <div className="flex items-center space-x-4">
           <button onClick={handleAddTransaction} className="bg-[#4f46e5] text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm shadow-indigo-500/30 hover:bg-[#4338ca] transition-colors flex items-center space-x-1">
             <span className="material-symbols-outlined text-[18px]">add</span>
             <span className="hidden sm:inline">Lançar Manual</span>
             <span className="sm:hidden">Lançar</span>
           </button>
        </div>
      </div>

      {activeSubnav !== 'Planos' ? (
      <>
        {/* HERO & BALANCE CARD */}
        <div className="flex flex-col xl:flex-row justify-between items-start gap-8 mb-12">
           <div className="max-w-xl">
             <h2 className="text-5xl font-bold font-manrope text-slate-900 mb-4 tracking-tight">Fluxo Financeiro</h2>
             <p className="text-slate-500 font-medium leading-relaxed text-base">
               Precisão no rastreamento dos seus fluxos financeiros. Monitore e adicione entradas e saídas e concilie seus meses.
             </p>
           </div>

           {/* Monthly Balance Card Container wrapping actions config as well based on img 2 */}
           <div className="w-full xl:w-[480px]">
              <div className="bg-slate-50 rounded-xl p-4 flex flex-col items-center justify-center mb-0 relative shadow-sm border border-slate-100 h-full w-full">
                 
                 <div className="w-full flex justify-between items-center mb-6 relative">
                   <div className="flex items-center space-x-4 font-bold text-slate-800 relative">
                      <button className="flex items-center hover:bg-slate-200 rounded p-1" onClick={() => setIsMonthPickerOpen(!isMonthPickerOpen)}>
                         <span className="material-symbols-outlined text-lg">calendar_month</span>
                      </button>
                      <span className="text-lg tracking-tight uppercase cursor-pointer" onClick={() => setIsMonthPickerOpen(!isMonthPickerOpen)}>
                         {format(selectedMonth, 'MMMM yyyy', { locale: ptBR })}
                      </span>
                      
                      {isMonthPickerOpen && (
                        <div className="absolute top-10 left-0 bg-white shadow-xl rounded-xl z-50 p-2 border border-slate-100">
                          <DayPicker mode="single" month={selectedMonth} onMonthChange={setSelectedMonth} onSelect={(date) => { if (date) setSelectedMonth(date); setIsMonthPickerOpen(false); }} locale={ptBR} />
                        </div>
                      )}
                   </div>
                   <div className="flex space-x-2">
                      <button onClick={() => fileInputRef.current?.click()} className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 shadow-sm">
                        <span className="material-symbols-outlined text-[14px]">file_download</span>
                        <span>Importar OFX</span>
                      </button>
                   </div>
                 </div>

                 {/* The Purple Balance Box */}
                 <div className="w-full bg-[#4f46e5] text-white rounded-xl p-6 relative overflow-hidden shadow-md shadow-indigo-600/20">
                    <span className="material-symbols-outlined absolute -bottom-6 -right-4 text-9xl opacity-10 rotate-[-15deg]">account_balance_wallet</span>
                    <div className="relative z-10">
                      <p className="text-[10px] font-bold tracking-widest uppercase text-indigo-200 mb-1">Balanço do Mês (Real/Pendente)</p>
                      <h3 className="text-4xl font-bold font-manrope tracking-tight mb-6 flex items-center">
                         {isPositiveBalance ? '' : '- '}
                         {formatCurrency(Math.abs(monthlyBalance))}
                      </h3>
                      <p className="text-xs font-semibold text-indigo-200 bg-indigo-800/40 inline-flex px-2 py-1 rounded">
                        Emissão de {format(selectedMonth, 'MMM yyyy', { locale: ptBR })}
                      </p>
                    </div>
                 </div>

              </div>
           </div>
        </div>

        {/* FILTER TABS */}
        <div className="flex space-x-8 border-b border-slate-200 mb-8 overflow-x-auto no-scrollbar">
          {[
            { key: 'Lancamentos', label: 'Lançamentos' },
            { key: 'Pendentes', label: 'Pendentes / OFX' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-bold transition-colors whitespace-nowrap ${activeTab === tab.key ? 'text-[#4f46e5] border-b-2 border-[#4f46e5]' : 'text-slate-500 hover:text-slate-800'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TRANSACTIONS LIST */}
        <div className="space-y-10">
           {isLoading ? (
             <SkeletonLoader variant="transaction-list" count={6} />
           ) : filteredTransactions.length === 0 ? (
             <div className="bg-slate-50 p-12 rounded-xl border border-slate-100 text-center flex flex-col items-center">
                <span className="material-symbols-outlined text-4xl text-slate-300 mb-4">folder_off</span>
                <p className="text-slate-500 font-medium font-inter">Nenhuma transação cadastrada nesta visão.</p>
             </div>
           ) : (
             <div>
                <div className="space-y-2">
                   {filteredTransactions.map(tx => {
                     const isIncome = tx.type === 'INCOME';
                     return (
                     <div key={tx.id} className="group flex items-center justify-between p-4 hover:bg-slate-50 border border-transparent hover:border-slate-100 rounded-xl transition-colors">
                        <div className="flex items-center space-x-4 cursor-pointer" onClick={() => handleEdit(tx)}>
                           <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isIncome ? 'bg-green-100/50' : 'bg-indigo-100/50'}`}>
                              <span className={`material-symbols-outlined ${isIncome ? 'text-green-600' : 'text-[#4f46e5]'}`}>
                                {isIncome ? 'payments' : 'local_mall'}
                              </span>
                           </div>
                           <div>
                             <p className="font-bold text-slate-900 text-sm mb-0.5 group-hover:text-[#4f46e5] transition-colors">{tx.title}</p>
                             <p className="text-xs font-medium text-slate-500">
                               {tx.category ? tx.category.name : 'Sem Categoria'} &bull; {new Date(tx.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                             </p>
                           </div>
                        </div>
                        <div className="flex items-center space-x-6 text-right">
                           <div className={`hidden md:block px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md ${isIncome ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-700'}`}>
                             {tx.status === 'PENDING' ? 'Pendente' : 'Efetivado'}
                           </div>
                           <p className={`font-bold text-base font-manrope w-24 ${isIncome ? 'text-green-600' : 'text-slate-900'}`}>
                             {isIncome ? '+' : '- '} {formatCurrency(tx.amount || 0)}
                           </p>

                           {/* Action buttons — always visible on mobile, hover-only on desktop */}
                           <div className="flex items-center space-x-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                             {tx.status === 'PENDING' && (
                               <button onClick={() => handleConfirm(tx.id)} className="w-8 h-8 flex items-center justify-center bg-green-50 text-green-600 rounded-lg hover:bg-green-100" title="Confirmar">
                                 <span className="material-symbols-outlined text-[18px]">done</span>
                               </button>
                             )}
                             <button onClick={() => handleEdit(tx)} className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200" title="Editar">
                               <span className="material-symbols-outlined text-[18px]">edit</span>
                             </button>
                             <button onClick={() => handleDelete(tx.id)} className="w-8 h-8 flex items-center justify-center bg-red-50 text-red-600 rounded-lg hover:bg-red-100" title="Excluir">
                               <span className="material-symbols-outlined text-[18px]">delete</span>
                             </button>
                           </div>
                        </div>
                     </div>
                   )})}
                </div>

                {totalPages > 1 && (
                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-2">
                    <p className="text-xs font-medium text-slate-500">
                      Página {currentPage} de {totalPages} • {totalItems} registro(s)
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage <= 1}
                        className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage >= totalPages}
                        className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
             </div>
           )}
        </div>

        {/* TRANSACTION INTELLIGENCE BANNER */}
        <div className="mt-16 bg-white border border-slate-100 shadow-sm rounded-2xl p-8 flex flex-col md:flex-row gap-8 items-center justify-between overflow-hidden relative">
           <div className="w-full md:w-1/2 z-10">
              <p className="text-[10px] font-bold tracking-widest text-[#4f46e5] uppercase mb-4">Insights de Plataforma</p>
              <h3 className="text-3xl font-bold font-manrope text-slate-900 leading-tight mb-4 tracking-tight">
                 Mantenha seus custos sob controle neste mês.
              </h3>
              <p className="text-sm font-medium text-slate-600 mb-8 leading-relaxed max-w-sm">
                 Você pode utilizar os recursos do Dashboard e de Orçamentos Analíticos (Analytics) para avaliar e economizar nas categorias focais.
              </p>
              <button onClick={() => navigate('/app/reports')} className="bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-bold py-3 px-6 rounded-lg transition-colors">
                 Ver Relatórios Completos
              </button>
           </div>
           <div className="w-full md:w-1/2 h-64 rounded-xl overflow-hidden relative border border-slate-100 shadow-inner z-10 flex items-center justify-center bg-slate-900/5">
              <div className="absolute inset-0 bg-slate-900">
                 <img src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&q=80&w=1000" alt="Chart" className="w-full h-full object-cover opacity-60 mix-blend-screen" />
                 <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-80"></div>
              </div>
           </div>
        </div>
      </>
      ) : (
        /* RECURRING PLAN TAB - (If "Plans" is selected in top header) */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="text-xl font-bold font-manrope text-slate-900 mb-6">Nova Máquina Financeira</h3>
            <form className="space-y-4" onSubmit={handleRecurringSubmit}>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Título do Lançamento</label>
                <input name="title" type="text" value={recurringFormState.title} onChange={handleRecurringFormChange} placeholder="Ex: Conta de Luz" required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-primary" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Valor Fixo Mensal (R$)</label>
                <input name="amount" type="number" step="0.01" value={recurringFormState.amount} onChange={handleRecurringFormChange} required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-primary" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Tipo</label>
                  <select name="type" value={recurringFormState.type} onChange={handleRecurringFormChange} required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm font-bold text-slate-700">
                    <option value="EXPENSE">Despesa</option>
                    <option value="INCOME">Receita</option>
                  </select>
                </div>
                <div>
                   <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Dia do Vencimento</label>
                   <input name="day_of_month" type="number" min="1" max="31" value={recurringFormState.day_of_month} onChange={handleRecurringFormChange} required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm focus:ring-primary" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Categoria</label>
                <select name="category_id" value={recurringFormState.category_id} onChange={handleRecurringFormChange} required className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm">
                  <option value="" disabled>Selecione...</option>
                  {availableCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              <button type="submit" className="w-full bg-[#4f46e5] text-white py-3 rounded-xl font-bold shadow-md hover:bg-indigo-700 mt-2">Criar Automação</button>
            </form>
          </div>

          <div className="lg:col-span-2 bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="text-xl font-bold font-manrope text-slate-900 mb-6">Regras Ativas Associadas</h3>
            {isLoadingRecurring ? (
               <SkeletonLoader variant="recurring-list" count={3} />
            ) : recurringTransactions.length === 0 ? (
               <div className="text-center p-12 text-slate-500 bg-slate-50 rounded-xl">Sem regras automáticas criadas.</div>
            ) : (
               <div className="space-y-4">
                  {recurringTransactions.map(rec => (
                     <div key={rec.id} className="flex justify-between items-center p-4 border border-slate-100 rounded-xl hover:bg-slate-50">
                        <div className="flex items-center space-x-4">
                           <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#4f46e5]"><span className="material-symbols-outlined">repeat</span></div>
                           <div>
                              <p className="font-bold text-sm text-slate-900">{rec.title}</p>
                              <p className="text-xs text-slate-500">Próximo Venc.: Dia {rec.day_of_month}</p>
                           </div>
                        </div>
                        <div className="flex items-center space-x-4">
                           <p className={`font-bold font-manrope ${rec.type === 'INCOME' ? 'text-green-600' : 'text-slate-900'}`}>{rec.type === 'INCOME' ? '+' : '-'} {formatCurrency(rec.amount)}</p>
                           <button onClick={() => startDeleteRecurring(rec)} className="w-8 h-8 rounded text-red-500 hover:bg-red-50 flex items-center justify-center"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                        </div>
                     </div>
                  ))}
               </div>
            )}
          </div>
        </div>
      )}

      {/* --- MODALS RESTORED ALIVE --- */}
      <TransactionModal
        isOpen={isTransactionModalOpen}
        onClose={() => { setIsTransactionModalOpen(false); setDefaultType('EXPENSE'); }}
        onSave={handleSaveTransaction}
        editingTransaction={transactionToEdit}
        defaultType={defaultType}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message="Deseja mesmo deletar este registro de transação?"
      />

      <ConfirmModal
        isOpen={isRecurringConfirmOpen}
        onClose={() => setIsRecurringConfirmOpen(false)}
        onConfirm={confirmDeleteRecurring}
        title="Deletar Máquina"
        message={`Deseja desativar a automação "${recurringToDelete?.title}" para sempre?`}
      />

    </div>
  );
}

export default TransactionsPage;